import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useGetScopesforFieldsQuery } from '../../../api/formsApiSlice';
import { useToast } from '../../toast/ToastContext';
import { FormActions, FormContainer, ModalContent, ModalOverlay } from './FormFiller';
import FormTitle from '../../form/FormTitle';
import { FieldLabel, ErrorText } from '../FieldSettings/FieldSettingsModal';
import CustomInput from '../../form/CustomInput';
import CustomTextArea from '../../form/CustomTextArea';
import StyledText from '../../StyledComponents/StyledText';
import { CorporateTheme, Theme } from '../../../utils/themes';
import CloseIcon from '../../../assets/icons/CloseIcon';
import { Button } from '../../form/Button';
import DocumentIcon from '../../../assets/icons/document-text';
import { useTheme } from '../../../middleware/ThemeContext';
import { Field, Scope } from '../../../types/types';
import styled from 'styled-components';
import { hexToRgba } from '../../../utils/helper';
import AMSSelect from '../../AMSSelect';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { ComponentFieldChips, CrossIconContainer, DraggableIconContainer } from '../../../features/forms/Components/CreateComponentModal';
import DraggableIcon from '../../../assets/icons/DraggableIcon';
import CrossIcon from '../../../assets/icons/CrossIcon';
import { SkeletonCell } from '../../SkeletonCell';
import { useCreateWidgetMutation, useGetSingleWidgetsQuery, useUpdateWidgetMutation } from '../../../api/widgetApiSlice';
import ToggleSwitch from '../../StyledComponents/StyledToggle';
import AddIcon from '../../../assets/icons/AddIcon';
import { LoadingSpinner } from '../../../features/ui/LoadingSpinner';
// ...existing code...

interface CreateWidgetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  fieldId?: number | null; 
  isSystemWidget?: boolean;
  isView?: boolean;
}

interface ComponentField {
  id: string;
  name: string;
  position: number;
  layoutType: 'row' | 'column';
  positionInRow: 'left' | 'right';
  fieldId?: string; // optional for separators
  type: 'field' | 'separator'; // NEW
  parentId: string | null;    // NEW: null = root level

}

const FIELDS_PAGE_SIZE = 20;

const CreateWidgetModal: React.FC<CreateWidgetModalProps> = ({ isSystemWidget, isOpen, onClose, onSuccess, fieldId, isView }) => {

  const { theme } = useTheme();
  const showToast = useToast();
  const [componentFields, setComponentFields] = useState<ComponentField[]>([]);
  const [showSeparatorAt, setShowSeparatorAt] = useState<number | null>(null);
  const [componentNameError, setComponentNameError] = useState<string | null>(null);
  const [active, setActive] = useState(true);
  const [componentFieldsError, setComponentFieldsError] = useState<string | null>(null);
  // ...existing code...
  const [scopeError, setScopeError] = useState<string | null>(null);
  const [allScopes, setAllScopes] = useState<{ label: string; value: string }[]>([]);
  const scopesAdditionalParams = useMemo(() => ({ isVisible: true }), []);
  const [{ name, description, scope }, setStateFormDetails] = useState<{
    name: string;
    description: string;
    scope: number | undefined;
  }>({
    name: '',
    description: '',
    scope: undefined,
  });

  const scopeIdForDropdown = scope ? String(scope) : '';
  const fieldSelectDisabled = !scopeIdForDropdown;
  const fieldSelectParams = fieldSelectDisabled ? undefined : { scopeId: scopeIdForDropdown };
  const fieldSelectKey = scopeIdForDropdown || 'no-scope';

  const selectedFieldOptions = useMemo(
    () => componentFields
      .filter(field => field.type === 'field')
      .map(field => ({
        value: String(field.id),
        label: field.name || `Field ${field.id}`
      })),
    [componentFields]
  );

  const selectedScopeOption = useMemo(() => {
    if (!scope) return null;
    const scopeValue = String(scope);
    const existing = allScopes.find((scopeItem) => scopeItem.value === scopeValue);
    const label = existing?.label || `Scope ${scopeValue}`;
    return { value: scopeValue, label };
  }, [scope, allScopes]);

  const mapFieldsResponse = useCallback((json: any, pageNumber: number) => {
    const fields = json?.fields ?? [];
    const perPage = json?.perPage ?? json?.pagination?.perPage ?? FIELDS_PAGE_SIZE;
    const totalCount = json?.totalCount ?? json?.pagination?.total ?? 0;
    const hasMore = totalCount ? pageNumber * perPage < totalCount : fields.length === perPage;

    const options = (fields as Field[]).map((field) => ({
      label: field.name,
      value: String(field.id),
      ...field,
    }));

    return {
      options,
      hasMore,
      additional: { page: pageNumber + 1 },
    };
  }, []);

  const mapScopesResponse = useCallback((json: any, pageNumber: number) => {
    const scopesList = json?.scopes ?? [];
    const perPage = json?.perPage ?? json?.pagination?.perPage ?? (scopesList.length || 1);
    const totalCount = json?.totalCount ?? json?.pagination?.total ?? scopesList.length;
    const hasMore = totalCount ? pageNumber * perPage < totalCount : scopesList.length === perPage;

    const options = (scopesList as Scope[])
      .filter((scopeItem) => scopeItem.displayName !== 'Event')
      .map((scopeItem) => ({
        label: scopeItem.displayName,
        value: String(scopeItem.id ?? ''),
        ...scopeItem,
      }));

    return {
      options,
      hasMore,
      additional: { page: pageNumber + 1 },
    };
  }, []);

  const handleFieldSelectionChange = useCallback((selectedOptions: any) => {
    const optionsArray = Array.isArray(selectedOptions)
      ? selectedOptions
      : selectedOptions
        ? [selectedOptions]
        : [];

    const selectedIds = new Set(
      optionsArray
        .map((option: any) => option?.id ?? option?.value ?? '')
        .filter(Boolean)
        .map((id: any) => String(id))
    );

    setComponentFields((prevFields) => {
      const filteredFields = prevFields.filter((item) => {
        if (item.type === 'separator') return true;
        return selectedIds.has(String(item.id));
      });

      const existingFieldIds = new Set(
        filteredFields
          .filter((item) => item.type === 'field')
          .map((item) => String(item.id))
      );

      const lastSeparator = [...filteredFields].reverse().find((item) => item.type === 'separator');
      const parentId = lastSeparator ? lastSeparator.id : null;

      const newFields = optionsArray
        .map((option: any) => {
          const rawId = option?.id ?? option?.value;
          if (rawId === undefined || rawId === null) return null;
          const id = String(rawId);
          if (existingFieldIds.has(id)) return null;
          return {
            id,
            name: option?.label || `Field ${id}`,
            layoutType: 'row' as const,
            positionInRow: 'left' as const,
            position: 0,
            fieldId: id,
            type: 'field' as const,
            parentId,
          };
        })
        .filter(Boolean) as ComponentField[];

      return [...filteredFields, ...newFields].map((item, idx) => ({
        ...item,
        position: idx + 1,
      }));
    });

    setComponentFieldsError(null);
  }, [setComponentFieldsError]);

  const handleScopeSelect = useCallback((option: any) => {
    const selectedOption = Array.isArray(option) ? option[0] : option;
    const nextValue = selectedOption?.value ? Number(selectedOption.value) : undefined;
    setStateFormDetails((prev) => ({ ...prev, scope: nextValue }));
    if (nextValue) {
      setScopeError(null);
      if (selectedOption?.label) {
        setAllScopes((prev) => {
          const exists = prev.some((scopeItem) => scopeItem.value === String(nextValue));
          if (exists) return prev;
          return [...prev, { label: selectedOption.label, value: String(nextValue) }];
        });
      }
    }
  }, [setScopeError, setAllScopes]);

  const [CreateWidget, { isLoading }] = useCreateWidgetMutation();
  const [updateWidget, { isLoading: isUpdating }] = useUpdateWidgetMutation();

  const {
    data: scopes,
    isFetching: isFetchingScopes,
  } = useGetScopesforFieldsQuery({ isVisible: true });

  useEffect(() => {
    if (scopes?.scopes) {
      const newOptions = scopes.scopes
        .filter((scopeItem: Scope) => scopeItem.displayName !== 'Event')
        .map((scopeItem: Scope) => ({
          label: scopeItem.displayName,
          value: String(scopeItem.id ?? ''),
        }));
      setAllScopes(newOptions);
    }
  }, [scopes]);


  const { data: widgetsData, isFetching: isFetchingWidgets, isLoading: isLoadingWidgetsData } = useGetSingleWidgetsQuery(fieldId, {
    skip: !fieldId,
    refetchOnMountOrArgChange: true,

  });




  const resetForm = () => {
    setComponentFields([]);
    setComponentNameError(null);
    setComponentFieldsError(null);
    setScopeError(null);
    setStateFormDetails({ name: '', description: '', scope: undefined });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };


  useEffect(() => {
    if (!scope) return;

    // 🔒 Edit mode → kuch bhi mat karo
    if (fieldId) return;

    // 🆕 Create mode → scope change hua, fields reset
    setComponentFields([]);
    setComponentFieldsError(null);

  }, [scope, fieldId]);


  const validateForm = () => {
    let isValid = true;

    // Reset errors
    setComponentNameError(null);
    setComponentFieldsError(null);
    setScopeError(null);

    // Validate component name
    if (!name.trim()) {
      setComponentNameError('Name is required');
      isValid = false;
    }

    // Validate fields count
    if (componentFields.length < 1 && !isSystemWidget) {
      setComponentFieldsError('At least one fields must be added in widget');
      isValid = false;
    }

    // Validate scope (only for new components)
    if (!scope) {
      setScopeError('Please select a scope for the component');
      isValid = false;
    }

    // Validate duplicate section (separator) names
    const separators = componentFields
      .filter((item) => item.type === 'separator')
      .map((item) => ({
        raw: (item.name ?? '').trim(),
        key: (item.name ?? '').trim().toLowerCase(),
      }))
      .filter((s) => Boolean(s.raw));

    if (separators.length > 0) {
      const counts = new Map<string, { count: number; label: string }>();
      for (const s of separators) {
        const existing = counts.get(s.key);
        if (existing) {
          counts.set(s.key, { count: existing.count + 1, label: existing.label });
        } else {
          counts.set(s.key, { count: 1, label: s.raw });
        }
      }

      const duplicated = Array.from(counts.values())
        .filter((v) => v.count > 1)
        .map((v) => v.label);

      if (duplicated.length > 0) {
        const formattedDuplicates =
          duplicated.length === 1
            ? `${duplicated[0]} has duplicates`
            : duplicated.length === 2
              ? `${duplicated[0]} and ${duplicated[1]} have duplicates`
              : `${duplicated.slice(0, -1).join(', ')} and ${duplicated[duplicated.length - 1]} have duplicates`;

        showToast(
          'Validation Error',
          `Section name cannot be duplicated i.e ${formattedDuplicates}`,
          'error'
        );
        isValid = false;
      }
    }

    return isValid;
  };


  function transformPayload(items: ComponentField[]) {
    // Step 1: Extract separator info (id -> name)
    const separatorMap: Record<string, string> = {};
    for (const item of items) {
      if (item.id.startsWith('sep-') && item.type === 'separator') {
        separatorMap[item.id] = item.name;
      }
    }

    // Step 2: Filter only 'field' type items
    const fields = items
      .filter(item => item.type === 'field')
      .map(item => {
        if (item.parentId === null) {
          return { fieldId: Number(item.fieldId) };
        } else if (
          typeof item.parentId === 'string' &&
          item.parentId.startsWith('sep-') &&
          separatorMap[item.parentId]
        ) {
          return {
            fieldId: Number(item.fieldId),
            sectionName: separatorMap[item.parentId]
          };
        }
        // Optional: handle other parent types if needed, or ignore
        return null;
      })
      .filter(Boolean); // Remove any nulls

    return fields;
  }
  // const handleSubmit = async () => {
  //   if (!validateForm()) return;

  //   try {
  //     let call;
  //     if (isSystemWidget && fieldId) {
  //       updateWidget({ id: fieldId, data: { isActive: active } })
  //     }
  //     else {
  //       let payload = {
  //         name,
  //         description,
  //         scopeId: scope,
  //         isActive: active,
  //         fields: transformPayload(componentFields)
  //       };
  //       call = fieldId ? updateWidget({ id: fieldId, data: payload }) : CreateWidget(payload);

  //     }

  //     const data = await call?.unwrap();
  //     showToast("New Widget Created", `New widget ${name} has been created`, "success");
  //     onSuccess();
  //     handleClose();
  //   } catch (err: any) {

  //     const message =
  //       err?.data?.message ||
  //       "Failed to create widget. Please try again.";

  //     showToast("Widget Creation Failed", message, "error");
  //           handleClose();

  //   }
  // };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      let call;

      // let payload = {
      //   name,
      //   description,
      //   scopeId: scope,
      //   isActive: active,
      //   fields: transformPayload(componentFields)
      // };W

      if (isSystemWidget && fieldId) {

        call = updateWidget({ id: fieldId, data: { isActive: active } }); // assuming your API accepts { id, isActive } in body
      }
      else {
        const payload = {
          name,
          description,
          scopeId: scope,
          isActive: active, // optional: include only if backend allows it for non-system
          fields: transformPayload(componentFields)
        };

        call = fieldId
          ? updateWidget({ id: fieldId, data: payload })
          : CreateWidget(payload);
      }



      await call.unwrap();

      showToast(
        fieldId ? "Widget Updated" : "Widget Created",
        fieldId
          ? `Widget "${name}" has been updated`
          : `New widget "${name}" has been created`,
        "success"
      );

      onSuccess();
      handleClose();
    } catch (err: any) {
      const message =
        err?.data?.message || "Failed to save widget. Please try again.";

      showToast("Action Failed", message, "error");
    }
  };



  function transformApiResponseToLayout(apiResponse: any[]) {
    // Step 1: Separate root fields and sectioned fields
    const rootFields = [];
    const sections = new Map(); // sectionName => { fields: [], minPosition }

    for (const item of apiResponse) {
      const field = item.field;
      const fieldObj = {
        id: String(field.id),
        name: field.name,
        layoutType: "row",
        positionInRow: "left",
        position: item.position, // will be re-assigned later
        fieldId: String(field.id),
        type: "field",
        originalPosition: item.position,
        sectionName: item.sectionName,
        parentId: null
      };

      if (item.sectionName === null) {
        rootFields.push(fieldObj);
      } else {
        if (!sections.has(item.sectionName)) {
          sections.set(item.sectionName, {
            name: item.sectionName,
            fields: [],
            minPosition: item.position
          });
        }
        sections.get(item.sectionName).fields.push(fieldObj);
        // Update minPosition to earliest field in section
        sections.get(item.sectionName).minPosition = Math.min(
          sections.get(item.sectionName).minPosition,
          item.position
        );
      }
    }

    // Step 2: Sort root fields by position
    rootFields.sort((a, b) => a.originalPosition - b.originalPosition);

    // Step 3: Sort sections by their earliest field position
    const sortedSections = Array.from(sections.values()).sort(
      (a, b) => a.minPosition - b.minPosition
    );

    // Step 4: Build final array with separators and fields
    const result = [];
    let currentPosition = 1;

    // Add root fields first
    for (const field of rootFields) {
      field.position = currentPosition++;
      field.parentId = null;
      delete field.originalPosition;
      delete field.sectionName;
      result.push(field);
    }

    // Add each section (separator + fields)
    for (const section of sortedSections) {
      const sepId = `sep-${section.name}`;
      result.push({
        id: sepId,
        name: section.name,
        type: "separator",
        position: currentPosition++,
        parentId: null,
        layoutType: "row",
        positionInRow: "left"
      });

      // Sort section fields by original position
      section.fields.sort((a: any, b: any) => a.originalPosition - b.originalPosition);
      for (const field of section.fields) {
        field.position = currentPosition++;
        field.parentId = sepId;
        delete field.originalPosition;
        delete field.sectionName;
        result.push(field);
      }
    }

    return result;
  }


  useEffect(() => {
    if (widgetsData?.data?.widgetFields && fieldId) {
      const layoutArray = transformApiResponseToLayout(widgetsData?.data?.widgetFields || []);
      const statusId = Number(widgetsData.data.statusId);

      // const isActive = widgetsData?.data?.statusId === 17 ? false : true;
      const isActive = statusId !== 17;

      setComponentFields(layoutArray);
      setStateFormDetails({ name: widgetsData?.data?.name, description: widgetsData?.data?.description, scope: Number(widgetsData?.data?.scopeId) });
      setActive(isActive);
    }
  }, [widgetsData?.data?.widgetFields, fieldId]);

  const handleOnDragEnd = (result: any) => {
    const { source, destination } = result;

    if (!destination || source.index === destination.index) return;

    const items = Array.from(componentFields);
    const draggedItem = items[source.index];

    // ✅ Case 1: Agar user ne "separator" drag kiya
    if (draggedItem.type === 'separator') {
      // Step 1: Find all children of this separator (consecutive fields after it)
      const children: ComponentField[] = [];
      let i = source.index + 1;
      while (i < items.length && items[i].type !== 'separator') {
        if (items[i].parentId === draggedItem.id) {
          children.push(items[i]);
        }
        i++;
      }

      // Step 2: Remove separator + its children from original position
      const itemsWithoutSection = items.filter(
        item => item.id !== draggedItem.id && !children.some(c => c.id === item.id)
      );

      // Step 3: Insert at new position
      const newItems = [...itemsWithoutSection];
      newItems.splice(destination.index, 0, draggedItem, ...children);

      // ✅ Step 4: Reassign parentId for ALL fields that come AFTER the separator until next separator
      let currentParentId: string | null = null;
      const finalItems = newItems.map((item, idx) => {
        if (item.type === 'separator') {
          currentParentId = item.id;
          return item;
        } else {
          // If this field is after a separator, assign its parentId
          if (currentParentId !== null) {
            return { ...item, parentId: currentParentId };
          }
          // Else, it's root level
          return { ...item, parentId: null };
        }
      });
      // Step 5: Reassign positions
      const updatedItems = finalItems.map((item, idx) => ({
        ...item,
        position: idx + 1,
      }));

      setComponentFields(updatedItems);
      return;
    }

    // ✅ Case 2: Agar user ne "field" drag kiya
    const [movedField] = items.splice(source.index, 1);
    items.splice(destination.index, 0, movedField);

    // Find nearest separator above destination
    let newParentId: string | null = null;
    for (let i = destination.index - 1; i >= 0; i--) {
      if (items[i].type === 'separator') {
        newParentId = items[i].id;
        break;
      }
    }

    const updatedItems = items.map((item, idx) => {
      if (idx === destination.index) {
        return { ...item, parentId: newParentId };
      }
      return item;
    }).map((item, idx) => ({
      ...item,
      position: idx + 1,
    }));

    setComponentFields(updatedItems);
  };

  const handleRemoveField = (field: ComponentField) => {
    if (field.type === 'separator') {
      // Remove separator + all its children
      setComponentFields(prev =>
        prev.filter(f => f.id !== field.id && f.parentId !== field.id)
      );
    } else {
      // Regular field
      setComponentFields(prev => prev.filter(item => item.id !== field.id));
    }
  };


  const handleAddSeparatorAtOriginalIndex = (insertAfterOriginalIndex: number) => {
    const newSeparatorId = `sep-${Date.now()}`;
    const currentFields = [...componentFields];

    const newSeparator: ComponentField = {
      id: newSeparatorId,
      name: 'New Section',
      type: 'separator' as 'separator',
      position: insertAfterOriginalIndex + 2,
      parentId: null,
      layoutType: 'row' as 'row',
      positionInRow: 'left' as 'left',
    };

    currentFields.splice(insertAfterOriginalIndex + 1, 0, newSeparator);

    // Assign next fields (until next separator) as children
    let nextSeparatorIndex = currentFields.length;
    for (let i = insertAfterOriginalIndex + 2; i < currentFields.length; i++) {
      if (currentFields[i].type === 'separator') {
        nextSeparatorIndex = i;
        break;
      }
    }

    const updatedFields = currentFields.map((field, idx) => {
      if (idx > insertAfterOriginalIndex + 1 && idx < nextSeparatorIndex) {
        return { ...field, parentId: newSeparatorId };
      }
      return field;
    });

    const finalFields = updatedFields.map((f, i) => ({ ...f, position: i + 1 }));
    setComponentFields(finalFields);
  };

  const updateSeparatorName = (id: string, newName: string) => {
    setComponentFields(prev =>
      prev.map(f => (f.id === id ? { ...f, name: newName } : f))
    );
  };

  const getVisibleFieldsWithIndex = (fields: ComponentField[]) => {
    const visible: { field: ComponentField; originalIndex: number }[] = [];
    let currentParentId: string | null = null;

    for (let i = 0; i < fields.length; i++) {
      const field = fields[i];

      if (field.type === 'separator') {
        visible.push({ field, originalIndex: i });
        currentParentId = field.id;
      } else {
        if (field.parentId === currentParentId) {
          visible.push({ field, originalIndex: i });
        } else if (field.parentId === null) {
          visible.push({ field, originalIndex: i });
          currentParentId = null;
        }
      }
    }

    return visible;
  };
  const hanldeToggle = (checked: boolean) => {
    setActive(!active);
  }
  return (
    <Modal show={isOpen} onHide={handleClose} className='border-0'>
      <ModalOverlay>
        <ModalContent theme={theme}>
          <div className={'my-0'}>
              <FormTitle title={fieldId && !isView ? 'Update Widget' : fieldId && isView ? 'View Widget' : 'Create New Widget'} />
          </div>
          <FormContainer theme={theme}>
            <BlueFormContainer theme={theme}>
              <div className="px-3 py-2">
                <FieldLabel theme={theme}>
                  Widget Name <span style={{ color: 'red', fontWeight: 'bold', fontSize: '14px' }}>*</span>
                </FieldLabel>
                {(fieldId && isFetchingWidgets && isLoadingWidgetsData) ?
                  <SkeletonCell height="40px" className='mt-2' />
                  : <CustomInput
                    type="text"
                    placeholder="Enter widget name here"
                    value={name}
                    onChange={(value: string) => {
                      setStateFormDetails((prev) => ({ ...prev, name: value }));
                      setComponentNameError(null);
                    }}
                    disabled={isSystemWidget || isView}
                    error={componentNameError ?? undefined}
                    required
                  />}
              </div>

              <div className="px-3 py-2">
                <FieldLabel theme={theme}>Description</FieldLabel>
                {fieldId && isFetchingWidgets && isLoadingWidgetsData ? <SkeletonCell height="100px" className='mt-2' /> : <CustomTextArea
                  type="textarea"
                  placeholder="Enter form description..."
                  value={description}
                  disabled={isSystemWidget || isView}
                  onChange={(value: string) =>
                    setStateFormDetails((prev) => ({ ...prev, description: value }))
                  }
                />}
              </div>
              <div className="px-3 py-2">
                <FieldLabel theme={theme}>
                  Select Scope <span style={{ color: 'red', fontWeight: 'bold', fontSize: '14px' }}>*</span>
                </FieldLabel>
                {fieldId && isFetchingWidgets && isLoadingWidgetsData ? (
                  <SkeletonCell height="40px" className='mt-2' />
                ) : (
                  <AMSSelect
                    endpoint="/scopes"
                    placeholder="Select scope"
                    value={selectedScopeOption}
                    onChange={handleScopeSelect}
                    mapResponse={mapScopesResponse}
                    additionalParams={scopesAdditionalParams}
                    error={scopeError ?? undefined}
                    isSearchable={false}
                    isDisabled={Boolean(fieldId) || Boolean(isSystemWidget) || isView}
                    defaultOptions
                  />
                )}
              </div>

              <DashedBorder theme={theme}>
                <div className='text-center mt-4'>
                  <StyledText fontSize="16px" fontWeight={600} textColor={theme.CorporateTheme.colors.primary}>Select Fields For Widget</StyledText>
                  <StyledText fontSize='14px' fontWeight={500} textColor={theme.CorporateTheme.colors.textSecondary} className='mt-1'>Select multiple custom & system fields to add in this widget</StyledText>
                </div>
                {isFetchingWidgets || isLoadingWidgetsData ? (
                  <SkeletonCell height="40px" className='mt-4 mb-3' />
                ) : (
                  <>
                    <AMSSelect
                      className='mb-3 mt-4'
                      key={fieldSelectKey}
                      isMulti
                      showDataType
                      // showIcons
                      isSearchable
                      includeAccessKey
                      endpoint="/fields"
                      placeholder={fieldSelectDisabled ? 'Please select a scope first' : 'Select fields here'}
                      limit={FIELDS_PAGE_SIZE}
                      value={selectedFieldOptions}
                      onChange={handleFieldSelectionChange}
                      additionalParams={fieldSelectParams}
                      mapResponse={mapFieldsResponse}
                      isDisabled={fieldSelectDisabled || isSystemWidget || isFetchingScopes || isView}
                      defaultOptions={!fieldSelectDisabled}
                      getOptionValue={(option: any) => String(option?.id ?? option?.value ?? '')}
                      error={componentFieldsError ?? undefined}
                    />
                  </>

                )}

                {fieldId && isFetchingWidgets && isLoadingWidgetsData ?
                  Array(3).fill(0).map((item, index) => <SkeletonCell height="40px" className='mt-3' key={index} />)

                  :
                  <DragDropContext onDragEnd={handleOnDragEnd} >
                    <Droppable droppableId="componentFields">
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          style={{ position: 'relative' }}
                        >
                          {componentFields.length > 0 &&
                            getVisibleFieldsWithIndex(componentFields)?.map(({ field, originalIndex }, index) => (
                              <Fragment key={field.id}>
                                {/* Render "Add Separator" button ABOVE current item (except first) */}
                                {index > 0 && (
                                  <div
                                    className="separator-gap"
                                    onMouseEnter={() => setShowSeparatorAt(index)}
                                    onMouseLeave={() => setShowSeparatorAt(null)}
                                    style={{
                                      height: '1px',
                                      margin: '8px 0px',
                                      position: 'relative',

                                      background: showSeparatorAt === index && !isSystemWidget ? CorporateTheme?.colors?.blueberry : 'transparent',
                                      // margin: '4px 0',
                                    }}
                                  >
                                    {showSeparatorAt === index && !isSystemWidget && !isView && (

                                      <Button
                                        style={{
                                          position: 'absolute',
                                          top: '-11px',
                                          left: '50%',
                                          transform: 'translateX(-50%)',
                                          zIndex: 10,
                                          gap: '4px',
                                        }}
                                        radius='24px'
                                        height='24px'
                                        fontSize='10px'
                                        borderColor='none'
                                        theme={theme}
                                        btnBg={CorporateTheme?.colors?.blueberry}
                                        color={CorporateTheme?.colors?.background}
                                        type="button"
                                        $variant="customButton"
                                        onClick={() => {
                                          const originalIndex = getVisibleFieldsWithIndex(componentFields)[index - 1].originalIndex;
                                          handleAddSeparatorAtOriginalIndex(originalIndex);
                                        }}
                                      >
                                        <div><AddIcon color={CorporateTheme?.colors?.background} size={14} /></div>
                                        Add separator
                                      </Button>
                                    )}
                                  </div>
                                )}

                                <Draggable
                                  key={field.id}
                                  draggableId={String(field.id)}
                                  index={originalIndex}
                                  isDragDisabled={isSystemWidget || isView}
                                >
                                  {(draggableProvided) => {
                                    // Calculate indentation level
                                    const marginLeft = field.parentId ? 24 : 0;

                                    return (
                                      <div
                                        ref={draggableProvided.innerRef}
                                        {...draggableProvided.draggableProps}
                                        style={{
                                          ...draggableProvided.draggableProps.style,
                                          marginLeft,
                                        }}
                                      >
                                        {field.type === 'separator' ? (
                                          // Render editable separator
                                          <div
                                            className="separator-chip mb-2"
                                            ref={draggableProvided.innerRef}
                                            {...draggableProvided.draggableProps}
                                            {...draggableProvided.dragHandleProps}
                                            style={{
                                              padding: '8px 12px',
                                              background: hexToRgba(CorporateTheme.colors.blueberry, 0.2),
                                              // border: `1px solid ${CorporateTheme.colors.blueberry}`,
                                              borderRadius: '6px',
                                              display: 'flex',
                                              alignItems: 'center',
                                            }}
                                          >
                                            <DraggableIconContainer>
                                              <DraggableIcon color={CorporateTheme.colors.blueberry} />
                                            </DraggableIconContainer>
                                            <input
                                              type="text"
                                              value={field.name}
                                              onChange={(e) => updateSeparatorName(field.id, e.target.value)}
                                              style={{
                                                background: 'transparent',
                                                border: 'none',
                                                outline: 'none',
                                                fontWeight: '600',
                                                flex: 1,
                                                marginLeft: '8px',
                                                fontSize: '14px',
                                                color: CorporateTheme.colors.blueberry,
                                              }}
                                              disabled={isSystemWidget}
                                              onMouseDown={(e) => e.stopPropagation()}
                                              onTouchStart={(e) => e.stopPropagation()}
                                            />
                                            <CrossIconContainer onClick={() => { if (!isSystemWidget) handleRemoveField(field) }}>
                                              <CrossIcon size={16} color={theme.CorporateTheme.colors.headerColor} />
                                            </CrossIconContainer>
                                          </div>
                                        ) : (
                                          // Regular field chip
                                          <ComponentFieldChips
                                            theme={theme}
                                            // className="mb-3"
                                            {...draggableProvided.dragHandleProps}
                                          >
                                            <DraggableIconContainer>
                                              <DraggableIcon color={theme.CorporateTheme.colors.lightSecondary} />
                                            </DraggableIconContainer>
                                            <StyledText fontSize="13px" fontWeight={600} textColor={theme.CorporateTheme.colors.lightSecondary}>
                                              {field.name || `Field ${field.id}`}
                                            </StyledText>
                                            <CrossIconContainer onClick={() => { if (!isSystemWidget) handleRemoveField(field) }}>
                                              <CrossIcon size={16} color={theme.CorporateTheme.colors.headerColor} />
                                            </CrossIconContainer>
                                          </ComponentFieldChips>
                                        )}
                                      </div>
                                    );
                                  }}
                                </Draggable>
                              </Fragment>
                            ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>}

              </DashedBorder>

              {/* Form Description */}

              <ToggleSwitch
                label="Do you want to show this widget"
                checked={active}
                activeColor={CorporateTheme.colors.primary}
                inactiveColor={CorporateTheme.colors.textSecondary}
                knobColor={CorporateTheme.colors.background}
                onChange={hanldeToggle}
                disabled={isView}
              />

            </BlueFormContainer>


          </FormContainer>

          {/* Actions */}
          <FormActions>
            <Button theme={theme} type="button" $variant="cancel" onClick={handleClose} disabled={isLoading || isUpdating}>
              <CloseIcon color={theme.CorporateTheme.colors.textSecondary} /> Close
            </Button>
            {!isView &&
            <Button theme={theme} type="submit" $variant="add" onClick={handleSubmit} disabled={isLoading || isUpdating}>
              {(isLoading || isUpdating) ? <LoadingSpinner color={theme.CorporateTheme.colors.background} size='16px' /> : <DocumentIcon color={theme.CorporateTheme.colors.background} />}
              {fieldId ? 'Update Widget' : 'Create Widget'}
            </Button>
            }
          </FormActions>
        </ModalContent>
      </ModalOverlay>
    </Modal>
  );
};

/* Styled components added for gap, widget box, drop area, etc. */
const BlueFormContainer = styled.div<{ theme: Theme }>`
  background-color: ${({ theme }) => hexToRgba(theme.CorporateTheme.colors.blueberry, 0.05)};
  margin: 15px;
  border : 1px solid ${({ theme }) => theme.CorporateTheme.colors.blueberry};
  border-left: 3px solid ${({ theme }) => theme.CorporateTheme.colors.blueberry};
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
  padding: 10px 0px;
`;

const DashedBorder = styled.div<{ theme: Theme }>`
  border: 1px solid ${({ theme }) => theme.CreateFormTheme.colors.inputBorders};
  border-style: dashed;
  border-radius: 5px;
  background: ${({ theme }) => theme.CreateFormTheme.colors.background};
  padding: 20px;
  margin : 10px 15px; 
`


/* ...existing code... */
export default CreateWidgetModal;